import { Trash2 } from 'lucide-react'
import { useRef, useState } from 'react'
import { useFormContext } from 'react-hook-form'

import { ImageCropDialog } from '@/components/dialogs/crop/image'
import { Button } from '@/components/ui/button'
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { UploadCardImagePreview } from '@/components/upload/card/image'
import useTranslator from '@/hooks/translation'
import { ACCEPTED_IMAGE_TYPES, CARD_ASPECT_RATIO } from '@/lib/constants'

export function ProjectsCreateStepOne({
  imgPreview,
  setImgPreview,
}: {
  imgPreview?: string
  setImgPreview: (imgPreview: string | undefined) => void
}) {
  const {
    control,
    setValue,
    formState: { errors },
  } = useFormContext()

  const { getTranslation } = useTranslator()

  const inputRef = useRef<HTMLInputElement>(null)

  const [inputKey, setInputKey] = useState(0)
  const [showCrop, setShowCrop] = useState(false)
  const [rawImage, setRawImage] = useState<string>()

  const handleClearPicture = () => {
    if (imgPreview) {
      URL.revokeObjectURL(imgPreview)
      setImgPreview(undefined)
    }

    if (rawImage) {
      URL.revokeObjectURL(rawImage)
      setRawImage(undefined)
    }

    setValue('picture', '', {
      shouldDirty: true,
      shouldValidate: true,
    })
    setInputKey((key) => key + 1)
  }

  const handleChangePicture = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0]
    if (!file) return

    const imageUrl = URL.createObjectURL(file)

    setRawImage(imageUrl)
    setShowCrop(true)
  }

  function handleCropImage(blob: Blob) {
    const originalFile = inputRef.current?.files?.[0]

    if (!originalFile) return

    const mimeType = originalFile.type

    const file = new File(
      [blob],
      `${originalFile.name}-${Date.now()}.${mimeType}`,
      {
        type: mimeType,
      },
    )

    const preview = URL.createObjectURL(file)

    setValue('picture', file, {
      shouldDirty: true,
      shouldValidate: true,
    })
    setImgPreview(preview)
    setShowCrop(false)
  }

  function handleCancelCropImage() {
    if (rawImage) {
      URL.revokeObjectURL(rawImage)
    }

    setRawImage(undefined)
    setInputKey((key) => key + 1)
    setShowCrop(false)
  }

  return (
    <div className="space-y-6 p-6 px-8 pt-4">
      <FormField
        control={control}
        name="picture"
        render={() => (
          <FormItem>
            <div className="space-y-1">
              <div className="relative mx-auto h-fit w-full">
                <FormLabel htmlFor="picture" className="h-fit w-fit space-y-2">
                  <span className="text-xs md:text-sm">
                    {getTranslation('common.image')}
                  </span>

                  <UploadCardImagePreview
                    key={inputKey}
                    imgPreview={imgPreview}
                    onEdit={() => inputRef.current?.click()}
                  />
                </FormLabel>

                {imgPreview && (
                  <Button
                    type="button"
                    variant="ghost"
                    className="absolute -bottom-2 -right-2 h-8 w-8 rounded-full border bg-background"
                    onClick={handleClearPicture}
                  >
                    <Trash2 />
                  </Button>
                )}
              </div>

              <FormControl>
                <Input
                  key={inputKey}
                  ref={inputRef}
                  id="picture"
                  type="file"
                  accept={ACCEPTED_IMAGE_TYPES.join(',')}
                  className="hidden"
                  onChange={handleChangePicture}
                />
              </FormControl>

              {errors.picture?.message && <FormMessage />}
            </div>
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="name"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-xs md:text-sm">
              {getTranslation('common.name')}
            </FormLabel>

            <div className="space-y-1">
              <FormControl>
                <Input type="text" placeholder="Project Name" {...field} />
              </FormControl>

              {errors.name?.message && (
                <span className="pl-2 text-xs font-medium text-destructive">
                  {getTranslation('common.validations.requiredValue')}
                </span>
              )}
            </div>
          </FormItem>
        )}
      />

      {showCrop && rawImage && (
        <ImageCropDialog
          imageSrc={rawImage}
          onCancel={handleCancelCropImage}
          onConfirm={handleCropImage}
          aspectRatio={CARD_ASPECT_RATIO}
        />
      )}
    </div>
  )
}
